import React from "react";
import { alpha, Alert, Box, Button, CircularProgress, Divider, Paper, Typography, useTheme } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canGenerateTranscript } from "@/shared/auth/authorization";

interface Props {
  /** Opens the additional-context dialog, which starts the job from there. */
  onGenerate: () => void;
  isGenerating: boolean;
  isMaxJobsReached: boolean;
  generateDisabled: boolean;
  /** Selected videos with no file in S3 whose fetch FAILED — terminal, bodycam never retries — re-add or remove. */
  failedVideoCount: number;
  /** Selected videos with no file in S3 still on their way down — "check again later" applies. */
  pendingVideoCount: number;
  viewedCount: number;
  totalVideos: number;
  onTranscribe: () => void;
  isTranscribing: boolean;
  transcribeDisabled: boolean;
  showTranscribeButton: boolean;
}

export const GenerateReportActions: React.FC<Props> = ({
  onGenerate,
  isGenerating,
  isMaxJobsReached,
  generateDisabled,
  failedVideoCount,
  pendingVideoCount,
  viewedCount,
  totalVideos,
  onTranscribe,
  isTranscribing,
  transcribeDisabled,
  showTranscribeButton,
}) => {
  const theme = useTheme();
  const canTranscribe = canGenerateTranscript(usePermissions());
  const hasUnavailableVideos = failedVideoCount > 0 || pendingVideoCount > 0;

  return (
    <Box>
      {hasUnavailableVideos ? (
        <Alert
          severity="warning"
          icon={<VideoLibraryIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            "& .MuiAlert-message": { fontSize: "0.85rem" },
          }}
        >
          {failedVideoCount > 0 && (
            <Typography variant="body2">
              {failedVideoCount === 1
                ? "One selected video couldn't be retrieved, so a report can't be generated from it."
                : "Some selected videos couldn't be retrieved, so a report can't be generated."}{" "}
              Re-add {failedVideoCount === 1 ? "it" : "them"} from the evidence page, or remove{" "}
              {failedVideoCount === 1 ? "it" : "them"} from the selection.
            </Typography>
          )}
          {pendingVideoCount > 0 && (
            <Typography variant="body2" sx={failedVideoCount > 0 ? { mt: 1 } : undefined}>
              {pendingVideoCount === 1
                ? "One selected video hasn't finished downloading, so a report can't be generated from it yet."
                : "Some selected videos haven't finished downloading, so a report can't be generated yet."}{" "}
              Please check again later.
            </Typography>
          )}
        </Alert>
      ) : generateDisabled ? (
        <Alert
          severity="warning"
          icon={<VideoLibraryIcon />}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            "& .MuiAlert-message": { fontSize: "0.85rem" },
          }}
        >
          <Typography variant="body2">
            Please review all {totalVideos} videos before generating ({viewedCount}/{totalVideos} viewed)
          </Typography>
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            color: "white",
            fontWeight: 700,
            fontSize: "1rem",
            flexShrink: 0,
          }}
        >
          3
        </Box>
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.2 }}
        >
          Generate Report
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Button
        variant="contained"
        onClick={onGenerate}
        color="primary"
        size="large"
        fullWidth
        disabled={isGenerating || isMaxJobsReached || generateDisabled || hasUnavailableVideos}
        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <BoltIcon />}
        sx={{
          py: 1.75,
          fontWeight: 700,
          borderRadius: 2.5,
          fontSize: "1rem",
          textTransform: "none",
          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
            transform: "translateY(-2px)",
          },
          "&:active": { transform: "translateY(0)" },
          "&:disabled": {
            background: theme.palette.grey[300],
            color: theme.palette.grey[500],
            boxShadow: "none",
          },
        }}
      >
        {isGenerating
          ? "Generating..."
          : isMaxJobsReached
            ? "Maximum Jobs Reached"
            : hasUnavailableVideos
              ? "Videos Not Ready"
              : generateDisabled
                ? `Review Videos (${viewedCount}/${totalVideos})`
                : "Generate"}
      </Button>

      {/* Hidden once the current video is already transcribed — the transcript
          overlay / Edit / Download surfaces in the player take over from here.
          Transcription is independent of the report-review gate, so it is not
          disabled by `generateDisabled` — only while a job is in flight. */}
      {showTranscribeButton && canTranscribe && (
        <Button
          variant="outlined"
          onClick={onTranscribe}
          color="primary"
          size="large"
          fullWidth
          disabled={transcribeDisabled}
          startIcon={isTranscribing ? <CircularProgress size={20} color="inherit" /> : <SubtitlesIcon />}
          sx={{
            mt: 1.5,
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2.5,
            fontSize: "0.95rem",
            textTransform: "none",
          }}
        >
          {isTranscribing ? "Transcribing..." : "Transcribe Video"}
        </Button>
      )}

      {isMaxJobsReached && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontWeight: 500, fontSize: "0.8rem" }}>
            Please wait for existing jobs to complete
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
